import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { bookDocId, dressDocId } from './inventoryApi';

function issueReceiptNo() {
  const d = new Date();
  const rand = String(Math.floor(Math.random() * 900) + 100);
  return `ISS-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${rand}`;
}

async function adjustBookCount(className, subject, delta) {
  const id = bookDocId(className, subject);
  const ref = doc(db, 'bookInventory', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`No book stock for ${subject} (class ${className}).`);
  const current = Number(snap.data().count) || 0;
  const next = current + delta;
  if (next < 0) throw new Error(`Insufficient book stock for ${subject} (have ${current}).`);
  await setDoc(ref, { count: next, updatedAt: serverTimestamp() }, { merge: true });
  return next;
}

async function adjustDressCount(dressType, size, delta) {
  const id = dressDocId(dressType, size);
  const ref = doc(db, 'dressInventory', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`No dress stock for ${dressType} size ${size}.`);
  const current = Number(snap.data().count) || 0;
  const next = current + delta;
  if (next < 0) throw new Error(`Insufficient dress stock for ${dressType} size ${size} (have ${current}).`);
  await setDoc(ref, { count: next, updatedAt: serverTimestamp() }, { merge: true });
  return next;
}

/**
 * Issue books/dress to students and reduce inventory.
 * lines: [{ type: 'book'|'dress', studentId, studentName, qty, class?, subject?, dressType?, size? }]
 */
export async function createInventoryIssueReceipt({ lines, notes, createdByUid, createdByName }) {
  if (!db) throw new Error('Firestore not configured.');
  if (!Array.isArray(lines) || lines.length === 0) throw new Error('Add at least one item.');

  const validLines = lines
    .map((l) => ({
      type: l.type === 'dress' ? 'dress' : 'book',
      studentId: String(l.studentId || '').trim(),
      studentName: String(l.studentName || '').trim(),
      qty: Math.max(1, Number(l.qty) || 1),
      class: String(l.class || '').trim(),
      subject: String(l.subject || '').trim(),
      dressType: String(l.dressType || '').trim(),
      size: String(l.size || '').trim(),
    }))
    .filter((l) => l.studentId && l.studentName);

  for (const line of validLines) {
    if (line.type === 'book') {
      if (!line.class || !line.subject) throw new Error('Book lines need class and subject.');
    } else if (!line.dressType || !line.size) {
      throw new Error('Dress lines need type and size.');
    }
  }

  if (validLines.length === 0) throw new Error('No valid lines.');

  const id = doc(collection(db, 'inventoryIssueReceipts')).id;
  const now = new Date().toISOString();

  for (const line of validLines) {
    if (line.type === 'book') {
      await adjustBookCount(line.class, line.subject, -line.qty);
    } else {
      await adjustDressCount(line.dressType, line.size, -line.qty);
    }
  }

  await setDoc(doc(db, 'inventoryIssueReceipts', id), {
    receiptNo: issueReceiptNo(),
    issuedAt: now,
    date: now.slice(0, 10),
    lines: validLines,
    notes: String(notes || '').trim(),
    createdByUid: createdByUid || null,
    createdByName: createdByName || null,
    createdAt: serverTimestamp(),
  });

  return { id, lineCount: validLines.length };
}

export async function listInventoryIssueReceipts() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'inventoryIssueReceipts'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.issuedAt || '').localeCompare(String(a.issuedAt || '')));
}

/** Delete issue receipt and restore stock counts. */
export async function deleteInventoryIssueReceipt(receiptId) {
  if (!db) throw new Error('Firestore not configured.');
  const id = String(receiptId || '').trim();
  if (!id) throw new Error('Receipt ID is required.');
  const snap = await getDoc(doc(db, 'inventoryIssueReceipts', id));
  if (!snap.exists()) throw new Error('Receipt not found.');
  const data = snap.data();
  for (const line of data.lines || []) {
    if (line.type === 'book') {
      await adjustBookCount(line.class, line.subject, Number(line.qty) || 1);
    } else if (line.type === 'dress') {
      await adjustDressCount(line.dressType, line.size, Number(line.qty) || 1);
    }
  }
  await deleteDoc(doc(db, 'inventoryIssueReceipts', id));
}
